import React, {useEffect, useRef} from 'react';
import {Controller, useFormState} from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from "@mui/material/Checkbox";
import {Chip} from "@mui/material";
import {Loader} from "../loaders";
import {FindLoad, getNestedError} from 'utils';
import {Svg} from 'assets'

export const CheckboxesTags = ({
                                   classNameWrapper,
                                   labelHeader,
                                   label,
                                   control,
                                   name,
                                   rules,
                                   multiple = false,
                                   placeholder,
                                   list = [],
                                   renderLabel,
                                   disabled,
                                   loading,
                                   loadType,
                                   StartIcon,
                                   disableClearable,
                                   selectedValue = 'id',
                                   allowError = true,
                                   handleChange,
                                   CustomLastElement,
                                   filterOptions,
                                   limitTags
                               }) => {
    const loader = FindLoad(loadType);
    const inputRef = useRef(null);
    const prevHasError = useRef(false);
    const {errors} = useFormState({control});
    const error = getNestedError(errors, name);
    const hasError = !!error?.type;

    useEffect(() => {
        if (hasError && !prevHasError.current && inputRef.current) {
            inputRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
        prevHasError.current = hasError;
    }, [hasError]);

    const normalizeValue = (value) => {
        if (multiple) {
            if (!Array.isArray(value)) return [];
            return value
                .map(v => {
                    if (!selectedValue) return list.find(i => i.id === v.id) || v;
                    return list.find(i => i[selectedValue] === v) || null;
                })
                .filter(Boolean);
        }
        if (!value) return null;
        if (!selectedValue) return list.find(i => i.id === value.id) || value;
        return list.find(i => i[selectedValue] === value) || null;
    };

    const safeGetOptionLabel = (option) => {
        if (!option) return '';
        if (typeof renderLabel === 'function') return renderLabel(option) ?? '';
        if (option.title) return String(option.title);
        if (option.name) return String(option.name);
        return JSON.stringify(option);
    };

    const getOptionId = (opt) => (selectedValue ? opt[selectedValue] : opt.id);

    const isOptionEqualToValue = (option, value) => {
        if (!option || !value) return false;
        if (multiple) {
            return selectedValue
                ? option[selectedValue] === value[selectedValue]
                : option.id === value.id;
        }

        return selectedValue
            ? option[selectedValue] === value[selectedValue]
            : option.id === value.id;
    };

    const handleSelectChange = (val, onChange) => {
        if (multiple) {
            const out = val ? val.map(opt => (selectedValue ? opt[selectedValue] : opt)) : [];
            onChange(out);
            handleChange?.(out);
            return;
        }
        const out = val ? (selectedValue ? val[selectedValue] : val) : null;
        onChange(out);
        handleChange?.(out);
    };

    return (
        <div className={`input-main-wrapper input-custom-wrapper ${classNameWrapper || ''}`}>
            <Controller
                control={control}
                name={name}
                rules={rules}
                render={({field}) => {
                    const {value, onChange, ref} = field;
                    const normalizedValue = normalizeValue(value);

                    return (
                        <>
                            {labelHeader && (
                                <p className={`input-main-label-header ${error?.message ? 'errorLabel' : ''}`}>
                                    {labelHeader}
                                    {rules?.required && <span className="input-main-label-header-required">*</span>}
                                </p>
                            )}

                            <Autocomplete
                                limitTags={limitTags}
                                multiple={multiple}
                                disabled={disabled}
                                disableClearable={disableClearable}
                                filterOptions={filterOptions}
                                options={list}
                                value={normalizedValue}
                                onChange={(e, val) => handleSelectChange(val, onChange)}
                                getOptionLabel={safeGetOptionLabel}
                                isOptionEqualToValue={isOptionEqualToValue}
                                slotProps={{
                                    chip: {
                                        onClick: (e) => {
                                            e.stopPropagation();
                                        },
                                    },
                                }}
                                popupIcon={loader?.length || loading ? <Loader
                                    styles={{
                                        position: "absolute",
                                        right: 10,
                                        width: 15,
                                        height: 15,
                                        minWidth: 15,
                                        minHeight: 15,
                                    }}
                                /> : <Svg.SelectArrowSvg/>}
                                sx={{
                                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                        borderColor: error ? '#D72525' : '#9C9EA9',
                                    },
                                }}
                                renderOption={(props, option, {selected}) => {
                                    const idx = list.findIndex(i => getOptionId(i) === getOptionId(option));
                                    const isLast = idx === list.length - 1;

                                    return (
                                        <li
                                            {...props}
                                            key={getOptionId(option)}
                                            className={`main-select-option ${selected ? 'main-select-option-selected' : ''}`}
                                            style={option?.style ?? {}}
                                        >
                                            {multiple && (
                                                <Checkbox
                                                    style={{padding: 0, color: '#88C3E1'}}
                                                    checked={selected}
                                                />
                                            )}
                                            {safeGetOptionLabel(option)}
                                            {isLast && CustomLastElement && (
                                                <div
                                                    className="select-custom-last-element-wrapper">{CustomLastElement}</div>
                                            )}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => {
                                    const IconStart =
                                        list.find(i => i.name === params.inputProps.value || i.title === params.inputProps.value)?.Icon ||
                                        StartIcon;
                                    return (
                                        <TextField
                                            {...params}
                                            label={label}
                                            placeholder={placeholder}
                                            error={hasError}
                                            inputRef={(el) => {
                                                ref(el);
                                                inputRef.current = el;
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: IconStart ?
                                                    <IconStart/> : params.InputProps.startAdornment ?? null,
                                            }}
                                        />
                                    );
                                }}
                            />

                            {allowError && <p className="error-message">{error?.message}</p>}
                        </>
                    );
                }}
            />
        </div>
    );
};
